// "Translate tasks" on the Kanban toolbar (decision D37.3): rewrites the titles, descriptions and acceptance criteria
// of the tasks not started yet into the project's content language, with the planner's model. Always confirmed in a
// dialog; a paid planner model asks a second time (the worker ends the first run `needs_confirmation` and sends
// nothing). Requirement ids, links and code are kept by the worker. The API enforces the maintainer role.
import { useEffect, useState } from "react";
import { TASK_TRANSLATION_DEFAULT_CAP_CENTS } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { listTaskTranslations, requestTaskTranslation, type TaskTranslationList, type TaskTranslationRun } from "../../api/endpoints-content-language.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { Button } from "../../components/Button.tsx";
import { Banner, localErrorMessage } from "../../components/Feedback.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { GlobeIcon } from "../../components/icons.tsx";
import { contentLanguageLabel } from "./languages.ts";

const POLL_MS = 3000;
const cents = (n: number) => `${n}¢`;

export function TranslateTasks({ projectId, onTranslated }: { projectId: string; onTranslated: () => void }) {
  const list = useAsync((signal) => listTaskTranslations(projectId, signal), [projectId]);
  const latest = list.data?.items[0] ?? null;
  const active = latest?.state === "requested" || latest?.state === "running";
  // While a run waits or runs, poll; when it finishes, the board reloads once.
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => list.reload(), POLL_MS);
    return () => clearTimeout(timer);
  }, [active, latest?.id, list.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [finishedSeen, setFinishedSeen] = useState<string | null>(null);
  useEffect(() => {
    if (latest && latest.state === "completed" && latest.id !== finishedSeen) {
      if (finishedSeen !== null || latest.completed_at === null || Date.now() - Date.parse(latest.completed_at) < 60_000) onTranslated();
      setFinishedSeen(latest.id);
    }
  }, [latest?.id, latest?.state]); // eslint-disable-line react-hooks/exhaustive-deps
  return <TranslateTasksPanel data={list.data ?? null} request={(allowPaid) => requestTaskTranslation(projectId, allowPaid ? { allow_paid: true } : {}).then(() => list.reload())} />;
}

export interface TranslateTasksPanelProps {
  data: TaskTranslationList | null;
  request: (allowPaid: boolean) => Promise<unknown>;
}

/** The toolbar button, its confirmation dialog and the latest run's status (pure: data and the request come in). */
export function TranslateTasksPanel({ data, request }: TranslateTasksPanelProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const latest: TaskTranslationRun | null = data?.items[0] ?? null;
  const active = latest?.state === "requested" || latest?.state === "running";
  const pending = data?.pending_tasks ?? 0;
  const language = contentLanguageLabel(data?.content_language ?? "en");

  const send = async (allowPaid: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await request(allowPaid);
      setOpen(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const status = latest && latest.id !== dismissed ? latest : null;
  return (
    <>
      <Button size="sm" variant="ghost" icon={<GlobeIcon size={14} />} onClick={() => setOpen(true)} disabled={data === null || pending === 0 || active} data-testid="translate-tasks">
        {active ? t("translateTasks.running") : t("translateTasks.button")}
      </Button>
      {status && status.state !== "requested" && status.state !== "running" ? (
        <div className="kanban-toolbar-note" data-testid="translate-tasks-status">
          {status.state === "needs_confirmation" ? (
            <Banner
              tone="warning"
              role="alert"
              title={t("translateTasks.paidTitle")}
              action={
                <>
                  <Button size="sm" variant="ghost" onClick={() => setDismissed(status.id)}>
                    {t("common.dismiss")}
                  </Button>
                  <Button size="sm" variant="primary" loading={busy} onClick={() => void send(true)} data-testid="translate-tasks-confirm-paid">
                    {t("translateTasks.confirmPaid")}
                  </Button>
                </>
              }
            >
              <p>{t("translateTasks.paidBody", { model: status.model ?? "?", cap: cents(status.cost_cap_cents), count: pending })}</p>
              {error && !open ? <p role="alert">{localErrorMessage(error, t)}</p> : null}
            </Banner>
          ) : null}
          {status.state === "completed" ? (
            <Banner
              tone={status.tasks_skipped > 0 ? "warning" : "info"}
              title={t("translateTasks.done", { translated: status.tasks_translated, skipped: status.tasks_skipped })}
              action={
                <Button size="sm" variant="ghost" onClick={() => setDismissed(status.id)}>
                  {t("common.dismiss")}
                </Button>
              }
            >
              {status.error ? <p className="field-hint mono">{status.error}</p> : null}
            </Banner>
          ) : null}
          {status.state === "failed" ? (
            <Banner
              tone="error"
              role="alert"
              title={t("translateTasks.failed")}
              action={
                <Button size="sm" variant="ghost" onClick={() => setDismissed(status.id)}>
                  {t("common.dismiss")}
                </Button>
              }
            >
              {status.error ? <p className="field-hint mono">{status.error}</p> : null}
            </Banner>
          ) : null}
        </div>
      ) : null}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("translateTasks.confirmTitle", { language })}
        description={t("translateTasks.confirmBody", { count: pending, language, cap: cents(TASK_TRANSLATION_DEFAULT_CAP_CENTS) })}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t("common.back")}</Button>
            <Button variant="primary" loading={busy} onClick={() => void send(false)} data-testid="translate-tasks-confirm">
              {t("translateTasks.confirm")}
            </Button>
          </>
        }
      >
        <p className="field-hint">{t("translateTasks.kept")}</p>
        {error ? (
          <Banner tone="error" role="alert">
            {localErrorMessage(error, t)}
          </Banner>
        ) : null}
      </Dialog>
    </>
  );
}
